import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Flame, Leaf } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const MenuEditor = () => {
  const { vendorId } = useParams();
  const nav = useNavigate();
  const [sections, setSections] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await adminApi.getMenu(vendorId);
        setSections(m.sections || []);
        const vs = await adminApi.vendors();
        setVendorName(vs.find((v) => v.id === vendorId)?.name || vendorId);
      } catch (e) { toast.error('Failed to load menu'); }
    })();
  }, [vendorId]);

  const addSection = () => setSections((p) => [...p, { title: 'New section', items: [] }]);
  const removeSection = (idx) => setSections((p) => p.filter((_, i) => i !== idx));
  const updateSection = (idx, key, value) =>
    setSections((p) => p.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));

  const addItem = (sIdx) => {
    const id = `${vendorId}-${Date.now()}`;
    updateSection(sIdx, 'items', [
      ...sections[sIdx].items,
      { id, name: 'New item', description: '', price: 0, image: '', popular: false, veg: false },
    ]);
  };
  const removeItem = (sIdx, iIdx) => {
    updateSection(sIdx, 'items', sections[sIdx].items.filter((_, i) => i !== iIdx));
  };
  const updateItem = (sIdx, iIdx, key, value) => {
    updateSection(sIdx, 'items', sections[sIdx].items.map((it, i) => (i === iIdx ? { ...it, [key]: value } : it)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = sections.map((s) => ({
        title: s.title,
        items: s.items.map((it) => ({
          id: it.id || `${vendorId}-${Math.random().toString(36).slice(2, 8)}`,
          name: it.name,
          description: it.description || '',
          price: Number(it.price) || 0,
          image: it.image || '',
          popular: !!it.popular,
          veg: !!it.veg,
        })),
      }));
      await adminApi.putMenu(vendorId, cleaned);
      toast.success('Menu saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin/vendors" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4 mr-1" /> Vendors</Link>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Menu · {vendorName}</h1>
          <p className="text-slate-500 mt-1">Group items into sections. Changes are saved per-vendor.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={addSection}><Plus className="h-4 w-4 mr-1.5" />Add section</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save menu'}</Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {sections.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            No sections yet. <button className="text-emerald-700 underline" onClick={addSection}>Add first section</button>.
          </div>
        )}
        {sections.map((s, sIdx) => (
          <section key={s.id || `${s.title || 'section'}-${sIdx}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <Input value={s.title} onChange={(e) => updateSection(sIdx, 'title', e.target.value)} className="max-w-xs h-10 font-semibold" />
              <span className="text-xs text-slate-500">{s.items.length} items</span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => addItem(sIdx)}><Plus className="h-3.5 w-3.5 mr-1" />Add item</Button>
              <Button size="sm" variant="outline" onClick={() => removeSection(sIdx)} className="text-rose-600 hover:text-rose-700 border-rose-200"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove section</Button>
            </div>
            <div className="mt-4 space-y-3">
              {s.items.map((it, iIdx) => (
                <div key={it.id || iIdx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-slate-50 rounded-xl p-3">
                  <div className="md:col-span-3"><Label className="text-xs text-slate-600">Name</Label><Input value={it.name} onChange={(e) => updateItem(sIdx, iIdx, 'name', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-4"><Label className="text-xs text-slate-600">Description</Label><Input value={it.description} onChange={(e) => updateItem(sIdx, iIdx, 'description', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-1"><Label className="text-xs text-slate-600">$</Label><Input type="number" step="0.01" value={it.price} onChange={(e) => updateItem(sIdx, iIdx, 'price', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-3"><Label className="text-xs text-slate-600">Image URL</Label><Input value={it.image} onChange={(e) => updateItem(sIdx, iIdx, 'image', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-1 flex flex-col gap-1 pt-4">
                    <label className="flex items-center gap-1 text-xs text-slate-700"><input type="checkbox" className="accent-orange-500" checked={!!it.popular} onChange={(e) => updateItem(sIdx, iIdx, 'popular', e.target.checked)} /><Flame className="h-3 w-3 text-orange-500" /></label>
                    <label className="flex items-center gap-1 text-xs text-slate-700"><input type="checkbox" className="accent-emerald-600" checked={!!it.veg} onChange={(e) => updateItem(sIdx, iIdx, 'veg', e.target.checked)} /><Leaf className="h-3 w-3 text-emerald-600" /></label>
                  </div>
                  <div className="md:col-span-12 md:row-start-1 md:col-end-13 flex md:justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removeItem(sIdx, iIdx)} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
              {s.items.length === 0 && <div className="text-sm text-slate-500 text-center py-3">No items in this section.</div>}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default MenuEditor;
